import type { AnalyticsErrorCode } from "@/hooks/useAnalytics";
import { AgreementsApiError } from "@/integrations/agreements/client";

/** Campos do formulário de usuário → código de erro (GR-712, RN-05). */
const FIELD_CODES: Record<string, AnalyticsErrorCode> = {
  firstName: "campo_nome",
  lastName: "campo_sobrenome",
  email: "campo_email",
  phone_number: "campo_telefone",
  new_password: "campo_senha",
  temp_password: "campo_senha",
  current_password: "campo_senha",
};

/**
 * Traduz um erro de API em código de lista fechada para o GA4.
 *
 * RN-05: nunca derivar o código do texto devolvido pela API — ele pode conter
 * o e-mail digitado. Só `code` e `httpStatus` (anexados em
 * `integrations/keycloak/client.ts`) são usados.
 */
export const toApiErrorCode = (error: unknown): AnalyticsErrorCode => {
  const err = error as { code?: string; httpStatus?: number } | null;

  if (err?.code === "USER_ALREADY_EXISTS") return "usuario_ja_existe";
  if (typeof err?.httpStatus === "number") return `http_${err.httpStatus}`;

  // Sem status: a requisição não chegou a ter resposta (timeout, offline).
  return "erro_rede";
};

/**
 * Erro do musa-agreements (sob demanda) → código de lista fechada.
 *
 * RN-05: a mensagem do serviço é para o usuário e pode repetir o que ele digitou;
 * só o status HTTP viaja. Sem `AgreementsApiError` a requisição não teve resposta.
 */
export const toAgreementsErrorCode = (error: unknown): AnalyticsErrorCode =>
  error instanceof AgreementsApiError ? `http_${error.status}` : "erro_rede";

/**
 * Primeiro campo inválido de um formulário → código de erro.
 *
 * Recebe as chaves de `formState.errors` do react-hook-form. Retorna o código do
 * primeiro campo conhecido; se nenhum bater, cai em `campo_nome` como genérico
 * para não perder o registro do funil.
 */
export const toFormErrorCode = (
  errorFields: string[],
): AnalyticsErrorCode => {
  for (const field of errorFields) {
    const code = FIELD_CODES[field];
    if (code) return code;
  }
  return "campo_nome";
};
